import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Sparkles, Send, Bot, User } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Channel mana yang performanya terbaik minggu ini?",
  "Berikan rekomendasi konten untuk Instagram dan TikTok",
  "Kapan waktu terbaik posting di media sosial saya?",
  "Analisa penurunan traffic website dan berikan solusinya",
  "Buat ringkasan performa marketplace bulan ini",
  "Rekomendasi peningkatan SEO untuk website utama",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || streaming) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("dmcc_token");
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ message: q, session_id: sessionId }),
      });
      if (!resp.ok || !resp.body) throw new Error("Stream error");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const ev = JSON.parse(payload);
            if (ev.session_id && !sessionId) setSessionId(ev.session_id);
            if (ev.delta) {
              setMessages(m => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + ev.delta };
                return copy;
              });
            }
            if (ev.error) toast.error(ev.error);
          } catch {}
        }
      }
    } catch (err) {
      toast.error("Gagal mendapatkan respon AI");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="h-[calc(100vh-0px)] flex flex-col fade-in">
      <div className="px-6 lg:px-8 py-6 border-b border-[#1F1F1F] flex items-center justify-between">
        <div>
          <div className="label-mono">// ai analytics assistant</div>
          <h1 className="font-display font-black text-3xl tracking-tighter mt-1">
            AI <span className="text-[#00F0FF]">Assistant</span>
          </h1>
        </div>
        <div className="label-mono text-[#00E676] flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse" /> gpt-5.2 · online
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 lg:px-8 py-6">
        {messages.length === 0 ? (
          <div className="max-w-2xl mx-auto space-y-8 py-10">
            <div>
              <Sparkles className="text-[#00F0FF] mb-3" size={32} />
              <h2 className="font-display font-black text-3xl tracking-tighter">Halo, ada yang bisa saya bantu?</h2>
              <p className="text-zinc-500 mt-2">Saya AI Analytics Assistant Anda. Tanya apa saja tentang performa marketing, rekomendasi konten, atau strategi peningkatan.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => send(s)}
                  data-testid={`ai-suggestion-${i}`}
                  className="text-left p-4 border border-[#1F1F1F] hover:border-[#007AFF] hover:bg-[#0A0A0A] transition text-sm">
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-6">
            {messages.map((m, i) => (
              <div key={i} className="flex gap-3" data-testid={`ai-msg-${i}`}>
                <div className={`w-8 h-8 shrink-0 flex items-center justify-center border ${
                  m.role === "user" ? "border-[#1F1F1F] bg-[#0A0A0A]" : "border-[#007AFF] bg-[#007AFF]/10"
                }`}>
                  {m.role === "user" ? <User size={14} /> : <Bot size={14} className="text-[#00F0FF]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="label-mono mb-1">{m.role === "user" ? "you" : "assistant"}</div>
                  <div className={`whitespace-pre-wrap text-sm leading-relaxed ${m.role === "user" ? "text-zinc-300" : "text-white"}`}>
                    {m.content}
                    {i === messages.length - 1 && streaming && m.role === "assistant" && (
                      <span className="terminal-cursor" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-[#1F1F1F] bg-[#0A0A0A] p-4">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="max-w-3xl mx-auto flex items-center gap-2" data-testid="ai-form">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={streaming}
            placeholder="Ketik pertanyaan Anda..."
            data-testid="ai-input"
            className="flex-1 bg-[#050505] border border-[#1F1F1F] px-4 py-3 font-mono text-sm outline-none focus:border-[#007AFF]"
          />
          <button type="submit" disabled={streaming || !input.trim()}
            data-testid="ai-send-btn"
            className="bg-[#007AFF] hover:bg-[#0056b3] disabled:opacity-40 px-5 py-3 label-mono flex items-center gap-2">
            <Send size={14} /> kirim
          </button>
        </form>
      </div>
    </div>
  );
}
